import { COMPANY_NAME, PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Icon, Logo, type IconName } from "@sagot/ui";

const FEATURES: readonly { icon: IconName; title: string; body: string }[] = [
  { icon: "quote", title: "auth.aside.citeTitle", body: "auth.aside.citeBody" },
  { icon: "layers", title: "auth.aside.channelsTitle", body: "auth.aside.channelsBody" },
  { icon: "shield-check", title: "auth.aside.privacyTitle", body: "auth.aside.privacyBody" },
];

function SampleChat() {
  const { t } = useTranslation();
  return (
    <div className="mt-9 max-w-md rounded-lg border border-chrome-line bg-chrome-2 p-4" aria-hidden="true">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink-400">{t("auth.aside.sampleLabel")}</span>
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-mint-500">
          <span className="dot" />
          {t("auth.aside.live")}
        </span>
      </div>
      <div className="mt-3 flex justify-end">
        <div className="max-w-[80%] rounded-lg rounded-br-xs bg-brand-500 px-3 py-2 text-[13px] leading-snug text-ink-950">{t("auth.aside.sampleQuestion")}</div>
      </div>
      <div className="mt-2.5 flex items-start gap-2">
        <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-pill bg-brand-500 text-ink-950">
          <Icon name="sparkles" className="size-3" />
        </span>
        <div className="min-w-0">
          <div className="rounded-lg rounded-bl-xs bg-chrome-3 px-3 py-2 text-[13px] leading-snug text-chrome-fg">
            {t("auth.aside.sampleAnswerLead")} <strong className="text-white">{t("auth.aside.sampleAnswerAmount")}</strong> {t("auth.aside.sampleAnswerTail")}
            <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-xs bg-brand-500 px-1 align-text-top text-[10px] font-bold text-ink-950">1</span>
          </div>
          <div className="mt-1.5 inline-flex max-w-full items-center gap-1.5 rounded-md border border-chrome-line px-2 py-1 text-[11px] font-semibold text-chrome-muted">
            <Icon name="file-spreadsheet" className="size-3.5 text-brand-400" />
            <span className="truncate">{t("auth.aside.sampleSource")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Navy brand panel of the auth split layout (mockups/auth/auth.js ASIDE; hosting copy per DEC-011). */
export function AuthAside({ homeHref = "/" }: { homeHref?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col">
      <AppLink href={homeHref} className="inline-flex w-fit" aria-label={t("auth.aside.homeLabel", { product: PRODUCT_NAME })}>
        <Logo tone="light" size={30} />
      </AppLink>
      <div className="my-auto py-10">
        <span className="inline-flex items-center gap-1.5 rounded-pill border border-chrome-line bg-chrome-2 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-300">
          <Icon name="map-pin" className="size-3.5" />
          {t("auth.aside.pill")}
        </span>
        <p className="mt-5 max-w-xl font-display text-[34px] font-semibold leading-[1.15] tracking-tight text-white xl:text-[40px]">{t("auth.aside.headline")}</p>
        <ul className="mt-8 max-w-md space-y-4">
          {FEATURES.map((feature) => (
            <li key={feature.title} className="flex gap-3">
              <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-chrome-3 text-brand-400">
                <Icon name={feature.icon} className="size-4" />
              </span>
              <span>
                <span className="block text-sm font-bold text-white">{t(feature.title)}</span>
                <span className="block text-[13px] text-chrome-muted">{t(feature.body)}</span>
              </span>
            </li>
          ))}
        </ul>
        <SampleChat />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-chrome-muted">
        <span>{t("auth.aside.footer", { company: COMPANY_NAME })}</span>
        <span className="inline-flex items-center gap-1.5">
          <Icon name="lock" className="size-3.5" />
          {t("auth.aside.hosting")}
        </span>
      </div>
    </div>
  );
}
